import { BLOCK_KIND_NOUN } from '@features/activity/copy';
import type { AuditSurface } from '@root/@types/audit-log';

import { bold, Summary } from './primitives';
import type { SummarySlice } from './types';

const addedFromLibrary = (name: string | null, surface: AuditSurface, kindNoun: string) => (
  <Summary>
    {bold(name)}&nbsp;{kindNoun} added
    {surface !== 'template' ? <>&nbsp;from the library</> : null}
  </Summary>
);

export const blockSummaries = {
  'block.created': ({ entry, ctx }) => {
    const createdTargetName = entry.target?.name || null;

    if (entry.isAttachment) {
      return (
        <Summary>
          {bold(createdTargetName)}&nbsp;{ctx.kindNoun} added
        </Summary>
      );
    }

    return (
      <Summary>
        {bold(createdTargetName)}&nbsp;{ctx.kindNoun} created
      </Summary>
    );
  },
  'block.from_library': ({ entry, ctx }) => addedFromLibrary(ctx.targetName, entry.surface, ctx.kindNoun),
  'block.updated': ({ ctx }) => (
    <Summary>
      {bold(ctx.targetName)}&nbsp;{ctx.kindNoun} edited
    </Summary>
  ),
  'block.content_imported': ({ ctx }) => <Summary>File content imported into&nbsp;{bold(ctx.targetName)}</Summary>,
  'block.deleted': ({ ctx }) => (
    <Summary>
      {bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun} removed
    </Summary>
  ),
  'block.moved': ({ ctx }) => (
    <Summary>
      {bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun} moved
    </Summary>
  ),
  'block.swapped': ({ entry, ctx }) => {
    const otherName = entry.other.name || ctx.blockName(entry.other.id) || null;
    const otherKindNoun = BLOCK_KIND_NOUN(entry.surface, entry.other.kind);

    return (
      <Summary>
        Positions of {ctx.kindNoun}&nbsp;{bold(ctx.targetName)}&nbsp;and {otherKindNoun}&nbsp;{bold(otherName)}
        &nbsp;swapped
      </Summary>
    );
  },
  'block.renamed': ({ entry, ctx }) => (
    <Summary>
      {bold(entry.from)}&nbsp;{ctx.kindNoun} renamed to&nbsp;{bold(entry.to)}
    </Summary>
  ),
  'block.type_changed': ({ entry, ctx }) => (
    <Summary>
      {bold(ctx.targetName)}&nbsp;{ctx.kindNoun} type changed to&nbsp;{bold(entry.to)}
    </Summary>
  ),
  'block.clause_reference_changed': ({ entry, ctx }) =>
    entry.to ? (
      <Summary>
        Clause reference set to&nbsp;{bold(entry.to)}&nbsp;for&nbsp;{bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun}
      </Summary>
    ) : (
      <Summary>
        Clause reference cleared for&nbsp;{bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun}
      </Summary>
    ),
  'block.visibility_rule_added': ({ ctx }) => (
    <Summary>
      Conditional visibility rule added for&nbsp;{bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun}
    </Summary>
  ),
  'block.visibility_rule_edited': ({ ctx }) => (
    <Summary>
      Conditional visibility rule edited for&nbsp;{bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun}
    </Summary>
  ),
  'block.visibility_rule_removed': ({ ctx }) => (
    <Summary>
      Conditional visibility rule removed from&nbsp;{bold(ctx.targetName)}&nbsp;{ctx.plainKindNoun}
    </Summary>
  ),
} satisfies SummarySlice;
